import React, { useContext, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Badge, Button, Segmented, Select, Space, Tooltip } from 'antd';

import AgingDashboardContext from '@totem/components/ticketing/agingDashboard/AgingDashboardContext';
import AgingSummaryTab from '@totem/components/ticketing/agingDashboard/summary/AgingSummaryTab';
import AgingTicketsTab from '@totem/components/ticketing/agingDashboard/tickets/AgingTicketsTab';
import {
  AgingMeasure,
  TicketsDrill,
} from '@totem/components/ticketing/agingDashboard/types';
import {
  PRIORITIES,
  PRIORITY_LABELS,
} from '@totem/components/ticketing/agingDashboard/utilities';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';

const filterOption = (
  search: string,
  option?: { label: string; value: string },
) => (option?.label ?? '').toLowerCase().includes(search.toLowerCase());

export const AgingDashboardExtra = () => {
  const {
    filters,
    setFilters,
    measure,
    setMeasure,
    typeOptions,
    categoryOptions,
    loading,
    refresh,
  } = useContext(AgingDashboardContext);
  const { buildings } = useContext(OrganizationContext);

  const buildingOptions = (buildings ?? []).map((building) => ({
    value: building.id,
    label: building.name,
  }));

  return (
    <Space wrap>
      <Select
        mode="multiple"
        style={{ minWidth: 170, maxWidth: 320 }}
        options={buildingOptions}
        value={filters.buildingId}
        onChange={(values: string[]) => setFilters({ buildingId: values })}
        placeholder="All buildings"
        showSearch
        filterOption={filterOption}
        maxTagCount="responsive"
      />
      <Select
        mode="multiple"
        style={{ minWidth: 140, maxWidth: 280 }}
        options={typeOptions}
        value={filters.type}
        onChange={(values: string[]) => setFilters({ type: values })}
        placeholder="All types"
        maxTagCount="responsive"
      />
      <Select
        mode="multiple"
        style={{ minWidth: 140, maxWidth: 280 }}
        options={PRIORITIES.map((priority) => ({
          value: priority,
          label: PRIORITY_LABELS[priority],
        }))}
        value={filters.priority}
        onChange={(values: string[]) => setFilters({ priority: values })}
        placeholder="All priorities"
        maxTagCount="responsive"
      />
      <Select
        mode="multiple"
        style={{ minWidth: 150, maxWidth: 300 }}
        options={categoryOptions}
        value={filters.category}
        onChange={(values: string[]) => setFilters({ category: values })}
        placeholder="All categories"
        showSearch
        filterOption={filterOption}
        maxTagCount="responsive"
      />
      <Segmented<AgingMeasure>
        value={measure}
        onChange={setMeasure}
        options={[
          { value: 'update', label: 'Since last update' },
          { value: 'opened', label: 'Since opened' },
        ]}
      />
      <Tooltip title="Refresh">
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          aria-label="Refresh"
          onClick={refresh}
        />
      </Tooltip>
    </Space>
  );
};

const AgingDashboardLayout = () => {
  const { failed, data } = useContext(AgingDashboardContext);
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [drill, setDrill] = useState<TicketsDrill>({ view: 'breached' });

  const handleDrillDown = (next: TicketsDrill) => {
    setDrill(next);

    setActiveTab('tickets');
  };

  const needsUpdate = data?.kpis.stale7 ?? 0;

  return (
    <IBCard isCovered title="Ticket Aging" extra={<AgingDashboardExtra />}>
      {failed && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          title="Ticket aging could not be loaded."
        />
      )}
      <IBTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'summary',
            label: 'Summary',
            children: <AgingSummaryTab onDrillDown={handleDrillDown} />,
          },
          {
            key: 'tickets',
            label: (
              <Space size={6}>
                Tickets
                {needsUpdate > 0 && (
                  <Tooltip title="No update for 7+ days">
                    <Badge count={needsUpdate} color="orange" />
                  </Tooltip>
                )}
              </Space>
            ),
            children: (
              <AgingTicketsTab drill={drill} onDrillChange={setDrill} />
            ),
          },
        ]}
      />
    </IBCard>
  );
};

export default AgingDashboardLayout;
